import React from 'react';
import CloseButton from '../general/CloseButton';

const ExecutionModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  className = '', 
  size = 'large',
  noOverlay = false
}) => {
  if (!isOpen) return null;

  const modalClass = `pv-modal ${
    size === 'large' ? 'pv-modal--large' : 
    size === 'medium' ? 'pv-modal--medium' : 
    'pv-modal--auto'
  } ${className}`;

  const containerClass = `pv-modal-container ${className.includes('narrow-mode') ? 'pv-modal-container--narrow' : ''}`;

  return (
    <div className={containerClass}>
      {!noOverlay && (
        <div onClick={onClose} className="pv-modal-overlay" />
      )}
      <div className={modalClass}>
        <div className="pv-modal-header">
          <div className="pv-modal-title">
            <div className="pv-modal-title-accent" />
            <h3>{title}</h3>
          </div>
          <CloseButton onClick={onClose} size="sm" />
        </div>
        {children}
      </div>
    </div>
  );
};

const ExecutionNavigation = ({ 
  items, 
  activeItem, 
  onItemClick, 
}) => {
  return (
    <div className="pv-tabs">
      {items.map((item) => (
        <button
          key={item.key}
          onClick={() => onItemClick(item.key)}
          className={`pv-tab ${
            activeItem === item.key ? 'pv-tab--active' : ''
          }`}
          disabled={item.disabled}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};

const ExecutionContent = ({ children, className = '' }) => {
  return (
    <div className={`pv-section pv-execution-content ${className}`}>
      {children}
    </div>
  );
};

const ExecutionForm = ({ children, onSubmit, className = '' }) => {
  return (
    <div className="pv-card">
      <form className={`pv-form ${className}`} onSubmit={onSubmit}>
        {children}
      </form>
    </div>
  );
};

const ExecutionTable = ({ columns, data, renderActions, emptyMessage = 'No data available' }) => {
  const [sort, setSort] = React.useState({ key: null, dir: 'asc' });
  
  const sorted = React.useMemo(() => {
    if (!sort.key) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      return sort.dir === 'asc' 
        ? String(av).localeCompare(String(bv)) 
        : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [data, sort]);

  const toggleSort = (key) => {
    setSort((s) => (
      s.key === key 
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } 
        : { key, dir: 'asc' }
    ));
  };

  if (data.length === 0) {
    return (
      <div className="pv-card pv-card--empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="pv-card" style={{ overflow: 'hidden' }}>
      <table className="pv-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} onClick={() => toggleSort(c.key)}>
                <span className="pv-table-sortable">
                  {c.label}
                  {sort.key === c.key && (
                    <span className="arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </span>
              </th>
            ))}
            {renderActions && <th />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id}>
              {columns.map((c) => (
                <td key={c.key}>{c.render ? c.render(row) : row[c.key]}</td>
              ))}
              {renderActions && <td>{renderActions(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ExecutionActions = ({ children, align = 'right' }) => {
  return (
    <div className={`pv-form-actions pv-form-actions--${align}`}>
      {children}
    </div>
  );
};

export {
  ExecutionModal,
  ExecutionNavigation,
  ExecutionContent,
  ExecutionForm,
  ExecutionTable,
  ExecutionActions
};
